import { useTranslation } from "react-i18next";
import { AlertTriangle, Leaf, ListX, ShieldCheck, Sprout } from "lucide-react";
import { cn } from "@/lib/utils";
import { nombreCultivoConocido } from "@/components/pests/crop/nombreCultivo";
import type { LecturaIA } from "@/lib/pests/desenlaceIA";

/**
 * LO QUE LA IA DICE DE UNA FOTO, EN PASTILLAS (05-10-2026, plan cultivos SIEX F7,
 * «pantallas que faltaban»). Lo comparten la ficha del parte (`AiAnalysisCard`), el
 * escáner y la foto del asistente, para que las tres digan lo mismo:
 *
 *   · «Auxiliar · no es plaga» (verde): fauna útil; sin gravedad ni tratamiento.
 *   · «No es una plaga · fisiopatía o carencia» (azul): abiótico.
 *   · «Fuera de la lista de tomate» (ámbar): la plaga que nombró no está en la lista del
 *     cultivo; el nombre del cultivo sale de la lista curada (síncrona) o, si no se
 *     sabe, «del cultivo».
 *   · «Vigilancia en tu zona» (ámbar): salió de la vigilancia regional.
 *
 * Son etiquetas, no botones: no eligen ninguna plaga ni tocan ningún foco (F1/F6).
 */
const PASTILLA = "inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-tight";

export function AiOutcomeChips({
  lectura,
  cropCode = null,
  className,
}: {
  lectura: LecturaIA | null;
  /** El cultivo con que se analizó (código SIEX), para «Fuera de la lista de tomate». */
  cropCode?: number | null;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  if (!lectura) return null;
  const lang = i18n?.language ?? "es";
  const pastillas: Array<{ clave: string; clase: string; Icono: typeof Leaf; texto: string }> = [];
  if (lectura.desenlace === "auxiliar") {
    pastillas.push({
      clave: "beneficial",
      clase: "border-emerald-200 bg-emerald-50 text-emerald-800",
      Icono: Leaf,
      texto: t("pests.aiOutcome.beneficial", "Auxiliar · no es plaga"),
    });
  }
  if (lectura.desenlace === "abiotico") {
    pastillas.push({
      clave: "abiotic",
      clase: "border-sky-200 bg-sky-50 text-sky-800",
      Icono: Sprout,
      texto: t("pests.aiOutcome.abiotic", "No es una plaga · fisiopatía o carencia"),
    });
  }
  if (lectura.fueraDeLista) {
    const cultivo = cropCode != null ? nombreCultivoConocido(cropCode, [], lang) : null;
    pastillas.push({
      clave: "outside",
      clase: "border-amber-200 bg-amber-50 text-amber-800",
      Icono: ListX,
      texto: cultivo
        ? t("pests.aiOutcome.outsideCropList", "Fuera de la lista de {{crop}}", { crop: cultivo.toLocaleLowerCase(lang) })
        : t("pests.aiOutcome.outsideList", "Fuera de la lista del cultivo"),
    });
  }
  if (lectura.vigilancia) {
    pastillas.push({
      clave: "watch",
      clase: "border-amber-200 bg-amber-50 text-amber-800",
      Icono: AlertTriangle,
      texto: t("pests.crop.watchBadge", "Vigilancia en tu zona"),
    });
  }
  if (pastillas.length === 0) return null;
  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)} data-testid="ai-outcome-chips">
      {pastillas.map(({ clave, clase, Icono, texto }) => (
        <span key={clave} className={cn(PASTILLA, clase)} data-testid={`ai-outcome-${clave}`}>
          <Icono className="h-3 w-3 shrink-0" aria-hidden />
          <span className="min-w-0 break-words">{texto}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * Cuando la IA NO nombra nada: «La planta se ve sana», «La IA no puede saberlo con lo que
 * se ve» (con qué foto haría falta) o, en un análisis viejo, el «no se reconoció» de
 * siempre (`fallback`). Devuelve null si el análisis sí nombra algo.
 */
export function AiNoPestMessage({
  lectura,
  fallback,
  size = "sm",
  className,
}: {
  lectura: LecturaIA | null;
  /** El texto de siempre para un análisis sin `outcome` (modo abierto o viejo). */
  fallback: { title: string; body?: string };
  size?: "sm" | "lg";
  className?: string;
}) {
  const { t } = useTranslation();
  if (!lectura || lectura.conNombre) return null;
  let title = fallback.title;
  let body = fallback.body;
  let Icono: typeof Leaf | null = null;
  if (lectura.desenlace === "sana") {
    title = t("pests.aiOutcome.healthyTitle", "La planta se ve sana");
    body = t("pests.aiOutcome.healthyBody", "La IA no ve ninguna plaga.");
    Icono = ShieldCheck;
  } else if (lectura.desenlace === "no_se_sabe") {
    title = t("pests.aiOutcome.unclearTitle", "La IA no puede saberlo con lo que se ve");
    body = t("pests.aiOutcome.unclearBody", "Haz otra foto de cerca: la planta entera, el envés de las hojas y el fruto.");
  }
  const grande = size === "lg";
  return (
    <div className={cn("min-w-0", className)} data-testid="ai-no-pest" data-outcome={lectura.desenlace}>
      <p className={cn("flex items-start gap-1.5 font-semibold text-foreground", grande ? "justify-center text-lg" : "text-sm")}>
        {Icono && <Icono className={cn("shrink-0 text-emerald-600", grande ? "mt-1 h-5 w-5" : "mt-0.5 h-4 w-4")} aria-hidden />}
        <span className="min-w-0 break-words">{title}</span>
      </p>
      {body && <p className={cn("mt-0.5 break-words text-muted-foreground", grande ? "text-sm" : "text-xs")}>{body}</p>}
    </div>
  );
}

export default AiOutcomeChips;
